import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import BookingHistoryView from 'components/patient-entry/BookingHistoryView';
import { FormApi, FormState } from 'final-form';
import createDecorator from 'final-form-calculate';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Loader, Message, Popup } from 'semantic-ui-react';
import { getAllCovidTestAnalyzersForLab } from 'services/covidTestAnalyzerServices';
import { getInterventionBookingHistory } from 'services/interventionBookingServices';
import { getAllInterventions } from 'services/interventionServices';
import { confirmLabReception, getAllTestKitReceptionOptions, getCovidTestOverview } from 'services/labCentralServices';
import { fetchAllProducts } from 'services/productServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionBookingStatus, Urgency } from 'ts-types/api.enums';
import {
  ConfirmReceptionRequest,
  CovidTestAnalyzerDto,
  InterventionBookingHistoryDto,
  InterventionDto,
  LabCentralSearchResult,
  ProductDto,
  TestKitReceptionOptionDto
} from 'ts-types/api.types';
import { isValidBarcode } from 'utils/barcodeUtils';
import { isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { required } from 'utils/validatorUtils';

const StyledWarning = styled(Grid.Row)`
  color: #b58105;
  margin-top: 0.5rem;
`;


export const StyledTable = styled(VirtualizedTable)`
  .booking-history-icon-container {
    display: inline-block;
  }

  .expected-date {
    display: inline-block;
  }

  .expected-date.warn {
    color: #a76f0b;
  }

  .expected-date.overdue {
    color: darkred;
  }

  .icon-div {
    display: inline-flex;
  }
`;

const StyledColumn = styled(Grid.Column)`
  & .ui.button:first-child {
    margin-right: 1.5rem;
  }
`;

const validBarCode = (value: string) => {
  const required = value ? undefined : "required";
  if (required) {
    return required;
  }

  const validBarcode = isValidBarcode(value);
  return validBarcode ? undefined : "invalidCode";
};

interface TestKitReceptionFieldProps extends FormState {
  values: Partial<ConfirmReceptionRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

let initialValues: Partial<ConfirmReceptionRequest> = {};
let interventionMap: { [id: number]: InterventionDto } = {};
let productMap: { [id: string]: ProductDto } = {};

const TestKitReceptionView = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {getMessage, language} = props;

  const [pageDataLoaded, setPageDataLoaded] = useState(false);
  const [covidTestData, setCovidTestData] = useState([] as LabCentralSearchResult[]);

  const [laboratoryOptions, setLaboratoryOptions] = useState([] as DropdownOption[]);
  const [analyzerOptions, setAnalyzerOptions] = useState([] as DropdownOption[]);
  const [testKitReceptions, setTestKitReceptions] = useState([] as TestKitReceptionOptionDto[]);
  const [testKitReceptionOptions, setTestKitReceptionOptions] = useState([] as DropdownOption[]);

  const [interventionBookingHistory, setInterventionBookingHistory] = useState([] as InterventionBookingHistoryDto[]);
  const [interventionBookingHistoryLoading, setInterventionBookingHistoryLoading] = useState(false);

  const [inProgress, setInProgress] = useState(false);
  const [successfullyReceived, setSuccessfullyReceived] = useState(undefined as undefined | string);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    const fetchData = async () => {
      setPageDataLoaded(false);
      try {
        const laboratories = await getAllTestLaboratory(cancelTokenSource)
        const testKitReceptionOptions = await getAllTestKitReceptionOptions(cancelTokenSource);

        const basicLab = laboratories.find(lab => lab.basicLaboratory);
        initialValues = {
          laboratoryId: basicLab ? basicLab.id : undefined,
          testKitReceptionOptionId: testKitReceptionOptions.length ? testKitReceptionOptions[0].id : undefined
        }

        if (basicLab) {

          const analyzers: CovidTestAnalyzerDto[] = await fetchAndSetCovidTestAnalyzerOptions(basicLab.id);

          initialValues.analyzerId = analyzers.length ? analyzers[0].id : undefined;
        }

        const testKitReceptionOptionsDescription = (testKitReceptionOption: TestKitReceptionOptionDto) => {
          if (language === 'en') {
            return testKitReceptionOption.descriptionEn || testKitReceptionOption.description
          } else if (language === 'fr') {
            return testKitReceptionOption.descriptionFr || testKitReceptionOption.description
          } else if (language === 'it') {
            return testKitReceptionOption.descriptionIt || testKitReceptionOption.description
          }
          return testKitReceptionOption.description;
        }

        const interventions = await getAllInterventions(cancelTokenSource);
        const products = await fetchAllProducts(false, cancelTokenSource)

        interventionMap = _.keyBy(interventions, "id");
        productMap = _.keyBy(products, "id");

        setLaboratoryOptions(mapToDropdownOptionArray(laboratories, "name", "id"));
        setTestKitReceptions(testKitReceptionOptions);
        setTestKitReceptionOptions(mapToDropdownOptionArrayWithTextGetter(
            testKitReceptionOptions,
            testKitReceptionOption => testKitReceptionOptionsDescription(testKitReceptionOption),
            "id"))

      } catch (e) {
        handleError(e.response.data);
      } finally {
        setPageDataLoaded(true);
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const fetchAndSetCovidTestAnalyzerOptions = async (labId: number) => {

    const analyzers = await getAllCovidTestAnalyzersForLab(labId, cancelTokenSource);
    setAnalyzerOptions(mapToDropdownOptionArrayWithSep(analyzers, ["code", "description"], "id"));

    return analyzers;
  }

  const onFinally = () => {
    setInProgress(false)
  };

  const changeCovidTestAnalyzerOptions = useMemo(() => createDecorator({
    field: 'laboratoryId',
    updates: {
      analyzerId: async (fieldValue, allValues?: Partial<ConfirmReceptionRequest>) => {
        if (fieldValue) {
          try {
            const analyzers = await fetchAndSetCovidTestAnalyzerOptions(fieldValue);
            if (analyzers.length) {
              return analyzers[0].id;
            }
          } catch (e) {
            handleError(e.response.data);
          }
        }

        return undefined;
      }
    }
  }), []);

  const handleChange = debounce(({values}: TestKitReceptionFieldProps): void => {

    if (values.testKitCode && isValidBarcode(values.testKitCode)
        && (!covidTestData.length || (covidTestData[0].testKitCode !== values.testKitCode))) {

      setErrorMessage(undefined);
      setSuccessfullyReceived(undefined);
      setInProgress(true);

      getCovidTestOverview(values.testKitCode, cancelTokenSource)
          .then(response => setCovidTestData([response]))
          .catch((e) => {
            handleError(e.response.data);
            onFinally();
          })
          .finally(onFinally);
    } else if ((!values.testKitCode || !isValidBarcode(values.testKitCode))) {
      setCovidTestData([]);
    }
  }, 300);

  const resetFormAndCovidTestData = (form: FormApi) => {
    form.reset();
    setCovidTestData([]);
    setErrorMessage(undefined);
  };

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.canNotChangeBookingStatus,
      errorUtils.invalidBarcode,
      errorUtils.unknownBarcode,
      errorUtils.unknownInterventionBookingId,
      errorUtils.unknownTestKitReceptionOption,
      errorUtils.unknownPatient
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const handleSubmit = async (values: Partial<ConfirmReceptionRequest>, form: FormApi) => {

    if (covidTestData.length
        && (InterventionBookingStatus.AWAITING_COMPLETION === covidTestData[0].status
            || InterventionBookingStatus.EXECUTED === covidTestData[0].status)) {

      const selectedTestKitReceptionOption = testKitReceptions.find(tkr => tkr.id === values.testKitReceptionOptionId);
      const showLabAndAnalyzer = selectedTestKitReceptionOption
          && InterventionBookingStatus.LAB_RECEIVED === selectedTestKitReceptionOption.status;

      const request: Partial<ConfirmReceptionRequest> = {
        testKitCode: covidTestData[0].testKitCode,
        testKitReceptionOptionId: values.testKitReceptionOptionId!,
        laboratoryId: showLabAndAnalyzer ? values.laboratoryId! : undefined,
        analyzerId: showLabAndAnalyzer ? values.analyzerId! : undefined
      }

      setErrorMessage(undefined);

      await confirmLabReception(request, cancelTokenSource)
          .then(response => {
            setSuccessfullyReceived(response)

            const testKitReceptionOptionId = !_.isEmpty(testKitReceptionOptions)
                ? testKitReceptionOptions[0].value : undefined;
            const resetValues = {
              testKitReceptionOptionId: testKitReceptionOptionId,
              laboratoryId: request.laboratoryId,
              analyzerId: request.analyzerId
            }

            form.reset(resetValues);
          })
          .catch((e) => handleError(e.response.data));
    }
  };

  const renderConfirmReceptionFormSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            decorators={[changeCovidTestAnalyzerOptions]}
            subscription={{values: true, pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  };

  const renderForm = ({submitting, handleSubmit, form}: FormRenderProps): React.ReactNode => {

    const values = form.getState().values;
    const testKitReceptionOptionId = values.testKitReceptionOptionId;

    const disableBtn = (submitting || form.getState().hasValidationErrors)
        || !covidTestData.length
        || (InterventionBookingStatus.AWAITING_COMPLETION !== covidTestData[0].status
            && InterventionBookingStatus.EXECUTED !== covidTestData[0].status);

    const selectedTestKitReceptionOption = testKitReceptions.find(tkr => tkr.id === testKitReceptionOptionId);
    const showLabAndAnalyzer = selectedTestKitReceptionOption
        && InterventionBookingStatus.LAB_RECEIVED === selectedTestKitReceptionOption.status;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid columns={3}>
            {errorMessage &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                      {errorMessage}
                    </StyledErrorMessage>
                  </Grid.Column>
                </Grid.Row>
            }
            {successfullyReceived &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message success onDismiss={() => setSuccessfullyReceived(undefined)}>
                      <p>
                        {getMessage(`labCentral.${successfullyReceived}`)}
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("patientRecord.testResult.testKitCode")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="testKitCode"
                    component={Input}
                    validate={validBarCode}
                    autoFocus
                    fluid
                />
              </Grid.Column>
              <StyledColumn width={9}>
                <Button compact type="submit" primary disabled={disableBtn}>
                  {getMessage("labCentral.button.confirmReception")}
                </Button>
                <Button compact
                        type="button"
                        secondary
                        onClick={() => resetFormAndCovidTestData(form)}
                        disabled={submitting}>
                  {getMessage("button.reset")}
                </Button>
              </StyledColumn>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("labCentral.testKitReceptionOption")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="testKitReceptionOptionId"
                    component={Select}
                    options={testKitReceptionOptions}
                    validate={required}
                    fluid
                />
              </Grid.Column>
            </Grid.Row>
            {showLabAndAnalyzer &&
                <>
                  <Grid.Row>
                    <Grid.Column width={2} verticalAlign="middle">
                      {getMessage("labCentral.testlaboratory")}:
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="laboratoryId"
                          component={Select}
                          options={laboratoryOptions}
                          validate={required}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} verticalAlign="middle">
                      {getMessage("labCentral.covidTestAnalyzer")}:
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="analyzerId"
                          component={Select}
                          options={analyzerOptions}
                          validate={required}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </>
            }
            <FormSpy subscription={{values: true}} onChange={handleChange}/>
          </PxGrid>
        </form>
    )
  };

  const covidTestDataRowGetter = ({index}: any) => {
    Object.assign(covidTestData[index], {index: index + 1});
    return covidTestData[index];
  };

  const covidTestDataRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    let styles = {...style};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    );
  };

  const onStatusColumnClick = (bookingId: number) => {
    setInterventionBookingHistoryLoading(true);
    getInterventionBookingHistory(bookingId, cancelTokenSource)
        .then(response => {
          setInterventionBookingHistory(response);
        })
        .finally(() => {
          setInterventionBookingHistoryLoading(false);
        });
  };

  const renderInterventionBookingHistory = (data: any) => {
    const {rowData} = data;

    const trigger = (
        <div className="booking-history-icon-container">
          <div>
            <Icon
                className="booking-history-icon"
                name="history"
                onClick={() => onStatusColumnClick(rowData.bookingId)}/>
          </div>
        </div>
    )

    const loader = (
        <Loader active inline="centered" size="small">{getMessage("loading")}</Loader>
    );

    const content =
        interventionBookingHistoryLoading ? loader
            : <BookingHistoryView bookingHistory={interventionBookingHistory} currentStatus={rowData.status}/>

    return (
        <span>
      <Popup
          trigger={trigger}
          popperDependencies={[!!interventionBookingHistoryLoading]}
          popperModifiers={{
            preventOverflow: {
              boundary: document.getElementById("testKitReceptionMainContent")
            }
          }}
          content={content}
          on="click"
          size="small"
          position="left center"
      />
    </span>
    );
  }

  const testKitCodeCellRenderer = (data: any) => {
    const {cellData} = data;
    if (!cellData) {
      return "";
    }
    return cellData;
  };

  const interventionStatusCellRenderer = (data: any) => {
    const {rowData} = data;

    const status = rowData.status;

    return <div>
      {getMessage(`enum.interventionBookingStatus.${status}`)}
      &nbsp;{renderInterventionBookingHistory(data)}
    </div>;
  }

  const entryDateCellRenderer = (data: any) => {

    const {rowData} = data;

    const bookingDate = isoToDisplayDate(rowData.bookingDate);
    const bookingTime = rowData.bookingTime;
    const entryDateTimeTitle = `${bookingDate} - ${bookingTime}`;

    return (
        <div>
          <div className="date birth-date" title={entryDateTimeTitle}>
            {bookingDate} {bookingTime}
          </div>
        </div>
    )
  };

  const interventionCellRenderer = (data: any) => {
    const {rowData} = data;
    const {currentUser} = props;
    const intervention = interventionMap[rowData.interventionId];
    const interventionDescription = intervention ? intervention.description : "";
    const interventionColor = intervention ? intervention.color : undefined;

    let productText = "";
    let productTextLong = "";
    if (rowData.productId) {
      const product = productMap[rowData.productId];
      if (product) {
        const defaultDesc = !_.isEmpty(product.description) ? product.description : undefined;

        const texts: { [key: string]: string | undefined } = {
          "de": defaultDesc,
          "en": !_.isEmpty(product.descriptionEn) ? product.descriptionEn : defaultDesc,
          "fr": !_.isEmpty(product.descriptionFr) ? product.descriptionFr : defaultDesc,
          "it": !_.isEmpty(product.descriptionIt) ? product.descriptionIt : defaultDesc
        };

        const lang = currentUser ? currentUser.userConfig.language : "de";
        const desc = texts[lang] ? ` - ${texts[lang]}` : "";

        productText = `${product.id}`;
        productTextLong = `${product.id} ${desc}`;
      }
    }

    return (
        <div title={interventionDescription}>
          <div>
            {interventionColor &&
                <div className="icon-div">
                  <Icon
                      name="circle"
                      style={{color: `${interventionColor}`}}
                      size="small"
                      title={interventionDescription}
                  />
                </div>
            }
            <span>{interventionDescription}</span>
            {_.isEmpty(productText) ? "" : <span title={productTextLong}> ({productText})</span>}
          </div>
        </div>
    );
  }

  const inputChannelCellRenderer = (data: any) => {

    const {rowData} = data;

    const inputChannel = rowData.inputChannel;
    const inputChannelDisplay = getMessage(`enum.interventionBookingInputChannel.${inputChannel}`);

    return (
        <div>
          <div className="date birth-date" title={inputChannelDisplay}>{inputChannelDisplay}</div>
        </div>
    )
  };

  const patientBirthDateCellRenderer = (data: any) => {

    const {rowData} = data;

    const birthDate = rowData.patientBirthDate
        ? `${isoToDisplayDate(rowData.patientBirthDate)} (${rowData.patientAge})`
        : "";

    return (
        <div>
          <div className="date birth-date" title={birthDate}>{birthDate}</div>
        </div>
    )
  };

  const urgencyCellRenderer = (data: any) => {
    const {getMessage, getMessageWithNamedParams} = props;
    const {cellData, rowData} = data;

    const expectedResultTime = rowData.expectedResultTime;
    const expectedResultDate = rowData.expectedResultDate;
    let expectedResultDateTime = <></>;
    if (expectedResultDate && expectedResultTime) {

      const expectedResDate = moment(expectedResultDate)
      let numberOfDays = undefined;
      if (moment(expectedResultDate).isAfter()) {
        numberOfDays = expectedResDate.diff(moment(), 'days') + 1;
      }

      const expectedTime = `${expectedResultTime}`;
      const expectedDateTime = `${isoToDisplayDate(expectedResultDate)} - ${expectedTime}`;

      const expectedInstant = moment(`${expectedResultDate} ${expectedResultTime}`);
      const minutesToResult = expectedInstant.diff(moment(), 'minutes');
      let textClass = 'normal';
      if (minutesToResult > 0 && minutesToResult < 30) {
        textClass = 'warn';
      } else if (minutesToResult < 0) {
        textClass = 'overdue';
      }

      expectedResultDateTime = (
          <div className={`expected-date ${textClass}`} title={expectedDateTime}>
            {expectedResultTime}
            {numberOfDays && getMessageWithNamedParams(
                'patientCentral.overview.row.days',
                {numberOfDays: numberOfDays}
            )
            }
          </div>
      );
    }

    return cellData && cellData === Urgency.HIGH
        ? <>
          {expectedResultDateTime}
          <Icon name="exclamation triangle"
                color="red"
                style={{marginLeft: ".5rem"}}
                title={`${getMessage("patientCentral.checkIn.urgency")}: ${getMessage("enum.urgency.HIGH")}`}
          />
        </>
        : <>{expectedResultDateTime}</>;
  }

  const patientNameCellRenderer = ({rowData}: any) => {
    let patientFullName = "";
    if (rowData.patientLastName) {
      patientFullName = rowData.patientLastName;
    }

    if (rowData.patientFirstName) {
      patientFullName = `${patientFullName} ${rowData.patientFirstName}`
    }
    return patientFullName;
  }

  const companyTypeCellRenderer = ({rowData}: any) => {
    let companyType = "";

    if (rowData.companyType) {
      companyType = getMessage("enum.companyType." + rowData.companyType);
    }

    return companyType;
  }

  const cantonCellRenderer = ({rowData}: any) => {
    if (rowData.canton) {
      return rowData.canton;
    }

    return "";
  }

  const companyNameCellRenderer = ({rowData}: any) => {
    if (rowData.companyDescription) {
      return rowData.companyDescription;
    }

    return "";
  }

  const renderCovidTestDataSegment = (): JSX.Element => {

    const tableColumns = [
      {
        width: 100,
        flexGrow: 0,
        flexShrink: 0,
        label: "",
        dataKey: "urgency",
        cellRenderer: urgencyCellRenderer
      },
      {
        width: 80,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.patientId"),
        dataKey: "patientId"
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: `${getMessage("patientCentral.overview.tableHeader.patientFirstName")} ${getMessage("patientCentral.overview.tableHeader.patientLastName")}`,
        dataKey: "patientId",
        cellRenderer: patientNameCellRenderer
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: `${getMessage("labCentral.testkitReceptionTable.companyType")}`,
        dataKey: "companyType",
        cellRenderer: companyTypeCellRenderer
      },
      {
        width: 80,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("labCentral.testkitReceptionTable.canton"),
        dataKey: "canton",
        cellRenderer: cantonCellRenderer
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.company"),
        dataKey: "companyDescription",
        cellRenderer: companyNameCellRenderer
      },
      {
        width: 120,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.patientBirthDate"),
        dataKey: "patientBirthDate",
        cellRenderer: patientBirthDateCellRenderer
      },
      {
        width: 100,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.inputChannel"),
        dataKey: "inputChannel",
        cellRenderer: inputChannelCellRenderer
      },
      {
        width: 150,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("enum.testDateSearchBy.BOOKING_DATE"),
        dataKey: "bookingDate",
        cellRenderer: entryDateCellRenderer
      },
      {
        width: 120,
        flexGrow: 1,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.intervention"),
        dataKey: "interventionId",
        cellRenderer: interventionCellRenderer
      },
      {
        width: 170,
        flexGrow: 1,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.testKitCode"),
        dataKey: "testKitCode",
        cellRenderer: testKitCodeCellRenderer
      },
      {
        width: 130,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.status"),
        dataKey: "status",
        cellRenderer: interventionStatusCellRenderer
      }
    ];

    return (
        <StyledTable
            rowCount={covidTestData.length}
            rowGetter={covidTestDataRowGetter}
            rowRenderer={covidTestDataRowRenderer}
            rowHeight={42}
            columns={tableColumns}
        />
    );
  };

  const renderCovidTestDataWarnings = () => {
    if (covidTestData.length && covidTestData[0].warnings && covidTestData[0].warnings.length) {
      return (
          <PxGrid>
            {covidTestData[0].warnings.map((warning, index) => {
              return (
                  <StyledWarning key={index}>
                    <Grid.Column width={16}>
                      <Icon name="exclamation triangle" fitted/>&nbsp;
                      {getMessage(`labCentral.warning.${warning}`)}
                    </Grid.Column>
                  </StyledWarning>
              )
            })}
          </PxGrid>
      );
    }
    return;
  }

  return (
      <PraxStyledSegment style={{height: "100%"}} id="testKitReceptionMainContent">
        <div>
          {!pageDataLoaded
              ? <LoaderComponent message={getMessage("loading")}/>
              : renderConfirmReceptionFormSegment()
          }
        </div>
        {renderCovidTestDataWarnings()}
        <StyledViewTableRow>
          {inProgress
              ? <LoaderComponent message={getMessage("loading")}/>
              : covidTestData.length
                  ? renderCovidTestDataSegment()
                  : null}
        </StyledViewTableRow>
      </PraxStyledSegment>
  );

}

export default withMessages(withAuthContext(TestKitReceptionView));
